import { Dialog, DialogContent, DialogTitle, Divider } from "@material-ui/core";
import { Add, Delete, Visibility } from "@material-ui/icons";
import Close from "@material-ui/icons/Close";
import Button from "components/Forms/CustomButtons/ButtonTS";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import PopoverButton from "components/UI/CustomPopover/PopoverButton";
import Table from "components/Tables/ReactTable/ReactTable";
import React, { ChangeEvent, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateActivityUploadFile } from "services/input-core/document-attachment/upload-many-documents";
import { checkMessageErrors } from "utils/functions";
import { v4 as uuid } from "uuid";
import { useActivityItemContext } from "store/activityItem";

interface UploadModalProps {
  profile: "WORKER" | "SOW";
  modalOpen: boolean;
  fieldglassId?: string;
  onClose: () => void;
  onUploadSuccess?: (value: any) => void;
  setUploadType?: React.Dispatch<React.SetStateAction<boolean>>;
}

async function openImageInNewTab(
  currentFile: unknown,
  notficationCallback: () => void,
) {
  if (currentFile) {
    notficationCallback();

    const reader = new FileReader();
    reader.addEventListener("load", async (e) => {
      const base64String = e.target?.result;
      const win = window.open();
      win.document.write(
        '<iframe src="' +
          base64String +
          '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>',
      );
    });

    reader.readAsDataURL(currentFile as Blob);
  }
}

const ActionButtons: React.FC<{
  currentFile?: unknown;
  disabled?: boolean;
  handleDelete: () => void;
  notificationCallback: () => void;
  key: number;
}> = ({ notificationCallback, handleDelete, currentFile, key }) => {
  const { t } = useTranslation("modals");

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "flex-end",
      }}
    >
      <CustomPopover
        ref={customPopoverRef}
        identifier={`upload-document`}
        data-cy={`upload-content-popover-${key}`}
      >
        <PopoverButton
          data-cy={`preview-button`}
          onClick={() => {
            closePopoverFromParent();
            openImageInNewTab(currentFile, notificationCallback);
          }}
        >
          <Visibility style={{ fontSize: "1.2rem" }} />
          {t("preview")}
        </PopoverButton>
        <PopoverButton
          data-cy={`delete-button`}
          onClick={() => {
            closePopoverFromParent();
            handleDelete();
          }}
        >
          <Delete style={{ fontSize: "1.2rem" }} />
          {t("delete-document")}
        </PopoverButton>
      </CustomPopover>
    </div>
  );
};

const UploadDocumentModal: React.FC<UploadModalProps> = ({
  modalOpen,
  onClose,
  onUploadSuccess,
  setUploadType,
}) => {
  const { t } = useTranslation(["modals", "list-errors"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const columns: {
    Header: string;
    accessor: string;
  }[] = [
    {
      Header: t("react-table-column-name", { ns: "modals" }),
      accessor: "name",
    },
    {
      Header: t("react-table-column-actions", { ns: "modals" }),
      accessor: "actions",
    },
  ];

  const fileInput = React.useRef(null);
  const [tempFiles, setTempFiles] = React.useState([]);

  const { register, resetField, reset } = useForm();

  const {
    mutate: uploadFiles,
    isLoading: isUploadingFiles,
  } = useCreateActivityUploadFile();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files: unknown[] = [...fileInput.current.files];

    resetField("files");

    const filesWithId = files.map((file) => {
      const id = uuid();
      return { file, id };
    });
    setTempFiles((state) => [...state, ...filesWithId]);
  };

  const handleDelete = (id: string) => {
    setTempFiles((current) => current?.filter((e) => e.id !== id));
  };

  const dataFormatted =
    tempFiles?.map((doc, index) => {
      const { id, file } = doc;

      return {
        name: file.name,
        actions: (
          <ActionButtons
            key={index}
            handleDelete={() => handleDelete(id)}
            currentFile={file}
            notificationCallback={() =>
              toast.info(t("opening-preview", { ns: "modals" }))
            }
          />
        ),
      };
    }) || [];

  const handleSave = () => {
    toast.info(t("uploading-file", { ns: "modals" }));
    const formData = new FormData();

    const files = tempFiles;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    files.forEach((file: any) => {
      formData.append(
        "files",
        new File([file.file], encodeURI(file.file.name), {
          type: file.file.type,
        }),
      );
    });

    formData.append("_id", currentActivityItem?._id);

    uploadFiles(formData, {
      onSuccess: () => {
        setTempFiles([]);
        toast.success(t("uploading-success", { ns: "modals" }));
        if (onUploadSuccess) onUploadSuccess(true);
        if (setUploadType) setUploadType(false);
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          t(
            checkMessageErrors(
              error?.response?.data?.error,
              "ERR_UPLOADING_FAILED_DEFAULT",
            ),
            { ns: "list-errors" },
          ),
        );
      },
    });
  };

  const isButtonsDisabled = isUploadingFiles;

  const handleCloseModal = () => {
    fileInput.current.value = null;
    reset();
    setTempFiles([]);
    onClose();
  };

  return (
    <Dialog
      open={modalOpen}
      fullWidth
      maxWidth="md"
      scroll="body"
      onClose={handleCloseModal}
      disableBackdropClick
    >
      <DialogTitle>
        {t("add-files-to-activity", { ns: "modals" })}
        <Button
          justIcon
          simple
          color="tumblr"
          round
          size="sm"
          style={{
            position: "absolute",
            right: "10px",
            top: "10px",
          }}
          onClick={handleCloseModal}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "end",
          }}
        >
          <Button
            size="sm"
            color="primary"
            data-cy={`add-files-button`}
            onClick={() => {
              fileInput.current.value = null;
              fileInput.current.click();
            }}
          >
            <Add />
            {t("add-file", { ns: "modals" })}
          </Button>
        </div>
        <Divider />

        <Table
          columns={columns}
          data={dataFormatted}
          initialPageSize={5}
          columnsNotTrimFilters={["name"]}
        />

        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "24px",
            marginBottom: "24px",
          }}
        >
          <Button
            color="tumblr"
            data-cy={`cancel-button`}
            onClick={handleCloseModal}
            disabled={isButtonsDisabled}
            simple
          >
            {t("cancel", { ns: "modals" })}
          </Button>
          <Button
            color="success"
            data-cy={`save-button`}
            onClick={() => {
              handleSave();
            }}
            disabled={isButtonsDisabled || tempFiles.length === 0}
          >
            {t("save", { ns: "modals" })}
          </Button>
        </div>
      </DialogContent>
      <input
        style={{ display: "none" }}
        {...register("files")}
        type="file"
        ref={fileInput}
        onChange={handleUpload}
        multiple
        data-cy={`input-files`}
      />
    </Dialog>
  );
};

export default UploadDocumentModal;
